import { Field } from './ui.tsx';
import { t } from '../i18n/index.ts';
import { useSession } from '../lib/session.tsx';
import { ibanValid, routingValid } from '../screens/Bank.tsx';

/** Where payment runs send someone's money: an IBAN (SEPA), or a US routing and account number (ACH). */
export interface BankDetails {
  iban: string;
  bic: string;
  routing_no: string;
  account_no: string;
  account_type: '' | 'checking' | 'savings';
}

export const bankDetailsOf = (r?: { iban?: string | null; bic?: string | null; routing_no?: string | null; account_no?: string | null; account_type?: 'checking' | 'savings' | null } | null): BankDetails => ({
  iban: r?.iban ?? '',
  bic: r?.bic ?? '',
  routing_no: r?.routing_no ?? '',
  account_no: r?.account_no ?? '',
  account_type: r?.account_type ?? '',
});

const squash = (s: string) => s.replace(/\s+/g, '').toUpperCase();

/** What is wrong with the details, by field; empty fields are fine. */
export function bankDetailsErrors(v: BankDetails): Partial<Record<keyof BankDetails, string>> {
  const out: Partial<Record<keyof BankDetails, string>> = {};
  if (v.iban.trim() && /^[A-Za-z]{2}\d{2}/.test(squash(v.iban)) && !ibanValid(v.iban)) out.iban = t('bank.iban_wrong');
  if (v.bic.trim() && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(squash(v.bic))) out.bic = t('bank.bic_wrong');
  if (v.routing_no.trim() && !routingValid(v.routing_no)) out.routing_no = t('bank.routing_wrong');
  if (v.account_no.trim() && !/^[0-9A-Za-z-]{1,17}$/.test(v.account_no.replace(/\s/g, ''))) out.account_no = t('bank.account_no_wrong');
  return out;
}

/** The values to save: cleaned, and only the ones the business's payment file uses. */
export function bankDetailsValues(v: BankDetails, us: boolean) {
  return us
    ? { routing_no: v.routing_no.replace(/[\s-]/g, '') || null, account_no: v.account_no.replace(/\s/g, '') || null, account_type: v.account_type || null }
    : { iban: squash(v.iban) || null, bic: squash(v.bic) || null };
}

export function useUsBanking() {
  return useSession().tenant?.currency === 'USD';
}

export function BankDetailsFields({ value, onChange, disabled }: { value: BankDetails; onChange: (v: BankDetails) => void; disabled?: boolean }) {
  const us = useUsBanking();
  const errors = bankDetailsErrors(value);
  const set = (k: keyof BankDetails) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <fieldset className="bank-details">
      <legend className="field-label">{t('bankd.title')}</legend>
      <p className="muted small">{t(us ? 'bankd.hint_ach' : 'bankd.hint_sepa')}</p>
      <div className="form-grid">
        {us ? (
          <>
            <Field label={t('bank.routing_no')} error={errors.routing_no ?? null}>
              <input className="input tabular" inputMode="numeric" maxLength={11} value={value.routing_no} onChange={set('routing_no')} disabled={disabled} />
            </Field>
            <Field label={t('bankd.account_no')} error={errors.account_no ?? null}>
              <input className="input tabular" maxLength={20} value={value.account_no} onChange={set('account_no')} disabled={disabled} />
            </Field>
            <Field label={t('bankd.account_type')}>
              <select className="input" value={value.account_type} onChange={set('account_type')} disabled={disabled}>
                <option value="">{t('bankd.checking')}</option>
                <option value="savings">{t('bankd.savings')}</option>
              </select>
            </Field>
          </>
        ) : (
          <>
            <Field label={t('bank.iban')} error={errors.iban ?? null}>
              <input className="input tabular" maxLength={42} autoCapitalize="characters" spellCheck={false} value={value.iban} onChange={set('iban')} disabled={disabled} />
            </Field>
            <Field label={t('bank.bic')} hint={t('bankd.bic_hint')} error={errors.bic ?? null}>
              <input className="input tabular" maxLength={11} autoCapitalize="characters" spellCheck={false} value={value.bic} onChange={set('bic')} disabled={disabled} />
            </Field>
          </>
        )}
      </div>
    </fieldset>
  );
}
